import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import CombinedView, { CombinedShareFile } from '../project/CombinedView';
import { sharedSceneState, type SharedSceneState } from '../../utils/shareLink';
import styles from './PublicCombinedView.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

const REFUSALS: Record<Exclude<SharedSceneState, 'open'>, { icon: IconName; text: string }> = {
  linkOff: { icon: 'layers_clear', text: 'This link does not include the Scene.' },
  ownerOff: { icon: 'layers_clear', text: 'The Scene is not available for this shared project.' },
  empty: { icon: 'grain', text: 'Nothing in this shared project can be shown in the Scene.' },
};

const PublicCombinedView = () => {
  const { token } = useParams<{ token: string }>();

  const { data, loading, error } = useQuery(SHARED_LINK_QUERY, {
    variables: { token },
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return (
      <div className={styles.centerState}>
        <Icon name="refresh" className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  if (error || !data?.sharedLink) {
    return (
      <div className={styles.centerState}>
        <Icon name="link_off" className="text-5xl text-error mb-3" />
        <p className="text-on-surface-variant">This link is no longer valid.</p>
      </div>
    );
  }

  const link = data.sharedLink;

  const files: CombinedShareFile[] = (link.files as CombinedShareFile[]).map((f) => ({
    id: f.id,
    originalName: f.originalName,
    type: f.type,
    fileSize: f.fileSize,
    extension: f.extension,
    status: f.status,
    panoramaCount: f.panoramaCount,
    srs: f.srs ?? null,
  }));

  const sceneState = sharedSceneState(link);
  if (sceneState !== 'open') {
    const refusal = REFUSALS[sceneState];
    return (
      <div className={styles.centerState}>
        <Icon name={refusal.icon} className="text-5xl text-error mb-3" />
        <p className="text-on-surface-variant">{refusal.text}</p>
      </div>
    );
  }

  return (
    <CombinedView
      shareToken={token}
      sharedFiles={files}
      shareProjectId={link.project.id}
      shareProjectName={link.project.name}
      shareAccess={link.ownerAccess}
      guestMeasures={{ pointCloud: !!link.allowPointCloudMeasurements, ortho: !!link.allowOrthoMeasurements }}
      sharedProject={{
        id: link.project.id,
        epsg: link.project.epsg ?? null,
        latitude: link.project.latitude ?? null,
        longitude: link.project.longitude ?? null,
        basemap: link.project.basemap ?? null,
        sitePlacement: link.project.sitePlacement ?? null,
      }}
    />
  );
};

export default PublicCombinedView;
